/* eslint-disable no-param-reassign */
/* eslint no-console: ["error", { allow: ["error"] }] */
import { MapObjectV2 } from "@iventis/domain-model/model/mapObjectV2";
import { mappingApi } from "@iventis/api/src/api";
import { MapModuleLayer } from "@iventis/map-engine";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { createMapFromArray, parseRepeatedTimeRangeDataFieldValueToNumber } from "@iventis/utilities";
import { aggregatePagedResponseLists } from "@iventis/api-helpers";
import { PagedResult } from "@iventis/domain-model/model/pagedResult";
import { MapObjectRepeatedTimeRangeValue } from "@iventis/types";

export const searchMapObjectsByArea = async (
    bounds: number[][][],
    layers: MapModuleLayer[],
    mapId: string,
    level: number | string,
    dateFilter?: { time: number; day: number },
    abortSignal?: AbortSignal
): Promise<MapObjectV2[]> => {
    try {
        const params = {
            bounds,
            layerIds: layers.map((l) => l.id),
        };
        const mapObjects = await aggregatePagedResponseLists<MapObjectV2>(async (pageNumber) => {
            const { data } = await mappingApi.post<PagedResult<MapObjectV2[]>>(`/maps/${mapId}/map_objects/area_search?pageNumber=${pageNumber}&level=${level}`, params);
            return data;
        }, abortSignal);

        return filterMapObjectsByDateFilter(layers, mapObjects, dateFilter);
    } catch (err) {
        console.error("Error", err);
        return err;
    }
};

/**
 * Filters the map objects which are not between the date filter range
 */
export function filterMapObjectsByDateFilter(layers: MapModuleLayer[], mapObjects: MapObjectV2[], dateFilter?: { time: number; day: number }) {
    const notFilteredMapObjects: MapObjectV2[] = [];
    const mapOfLayers = createMapFromArray(layers, "id");

    for (const mapObject of mapObjects) {
        const layer = mapOfLayers.get(mapObject.layerId);

        // Layer does not contain repeated time ranges and therefore can't be filtered
        if (!layer?.dataFields.some(({ type }) => type === DataFieldType.RepeatedTimeRanges)) {
            notFilteredMapObjects.push(mapObject);
            continue;
        }

        const parsedDataFieldValue = [];
        let mapObjectFilteredByActiveDateFilter = false;

        for (const dataFieldValue of mapObject.dataFieldValues) {
            const dataField = layer.dataFields.find((df) => df.id === dataFieldValue.dataFieldId);
            // If datafield value is not a repeated time range
            if (dataField?.type !== DataFieldType.RepeatedTimeRanges) {
                parsedDataFieldValue.push(dataFieldValue);
                continue;
            }

            // Parse time range into something we can check if it needs to be filtered
            const repeatedTimeRangeValueNumber = parseRepeatedTimeRangeDataFieldValueToNumber(dataFieldValue.valueRepeatedTimeRanges);
            mapObjectFilteredByActiveDateFilter = dateFilter != null ? isTimeRangeFilteredByDateFilter(repeatedTimeRangeValueNumber, dateFilter) : false;

            // If it is being filtered, stop checking the datafield values
            if (mapObjectFilteredByActiveDateFilter) {
                break;
            }

            parsedDataFieldValue.push({ ...dataFieldValue, valueRepeatedTimeRanges: repeatedTimeRangeValueNumber });
        }

        if (!mapObjectFilteredByActiveDateFilter) {
            notFilteredMapObjects.push({
                ...mapObject,
                dataFieldValues: parsedDataFieldValue,
            });
        }
    }

    return notFilteredMapObjects;
}

function isTimeRangeFilteredByDateFilter(timeRanges: MapObjectRepeatedTimeRangeValue[], dateFilter: { time: number; day: number }) {
    return !timeRanges.some(
        (timeRangeValue) =>
            timeRangeValue.startDate <= dateFilter.day &&
            timeRangeValue.endDate >= dateFilter.day &&
            timeRangeValue.startTime <= dateFilter.time &&
            timeRangeValue.endTime >= dateFilter.time
    );
}
